import { GroupCondition } from '@/api/enums';
import { equal, group } from '@/api/helpers';
import {
  LandingPageTemplateFormState,
  PageTemplate,
} from '@/client/page-templates';
import { FormikInput, FormikRadio, FormikSwitch } from '@/components/form';
import { RedirectPageSelectInput } from '@/components/form/selectors';
import { landingPageTemplateSchema } from '@/components/landing-page-templates/forms/validations/landing-page-template';
import { useAppSelector } from '@/hooks/store';
import { selectCurrentAccount } from '@/store/features/account';
import { FlexContainer } from '@/ui/styled-ui';
import { Field, Form, Formik, FormikValues, useFormikContext } from 'formik';
import { InputText } from 'primereact/inputtext';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const StyledDiv = styled.div`
  border: 1px solid var(--gray-dark);
  padding: var(--default-padding);
  border-radius: var(--small-border-radius);
`;

type LandingPageTemplateInformationFormProps = {
  initialValues: LandingPageTemplateFormState;
  onSubmit: (data: FormikValues) => void;
  onInputChange: (values: LandingPageTemplateFormState) => void;
  onValidateForm: (isValid: boolean) => void;
  isSystem?: boolean;
};

type FormFieldsProps = Pick<
  LandingPageTemplateInformationFormProps,
  'onInputChange' | 'onValidateForm' | 'isSystem'
>;

const LandingFormFields: React.FC<FormFieldsProps> = ({
  onInputChange,
  onValidateForm,
  isSystem,
}) => {
  const { t } = useTranslation();
  const account = useAppSelector(selectCurrentAccount);
  const { values, isValid, setFieldValue } =
    useFormikContext<LandingPageTemplateFormState>();

  const activeFilters = account?.isSystem ? [] : [equal('active', true)];

  useEffect(() => {
    onValidateForm(isValid);
  }, [isValid, onValidateForm]);

  useEffect(() => {
    onInputChange(values);
  }, [values, onInputChange]);

  return (
    <FlexContainer justify="flex-start" align="flex-start" direction="column">
      <Form className="w-full">
        <div className="field w-6 mb-4">
          <Field
            id="name"
            name="name"
            label={t('generic.name')}
            placeholder={t('templates.nameOf')}
            className="w-full"
            component={FormikInput}
            required
          />
        </div>

        <div className="field-checkbox">
          <Field
            inputId="captureCredentials"
            name="captureCredentials"
            label={t('templates.enable.redirect')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setFieldValue('captureCredentials', e.target.value);
              if (!e.target.value) {
                setFieldValue('redirectType', 'url');
                setFieldValue('redirectUrl', null);
                setFieldValue('redirectPage', null);
              }
            }}
            component={FormikSwitch}
          />
        </div>
        <div
          className={`field w-6 mt-4 ${
            values.captureCredentials ? '' : 'hidden'
          }`}
        >
          <p
            style={{
              marginBottom: 'var(--xsmall-padding)',
              fontSize: 'var(--small-font-size)',
            }}
          >
            Redirect
          </p>
          <StyledDiv>
            <FlexContainer gap={24} justify="flex-start" className="mb-4">
              <div data-id="redirectTypeUrl">
                <Field
                  id="redirectTypeUrl"
                  type="radio"
                  name="redirectType"
                  value="url"
                  label={t('generic.url')}
                  shapeType="circle"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('redirectType', e.target.value)
                  }
                  checked={values.redirectType === 'url'}
                  component={FormikRadio}
                />
              </div>
              <div data-id="redirectTypeTemplate">
                <Field
                  id="redirectTypeTemplate"
                  type="radio"
                  name="redirectType"
                  value="template"
                  label={t('generic.template')}
                  shapeType="circle"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('redirectType', e.target.value)
                  }
                  checked={values.redirectType === 'template'}
                  component={FormikRadio}
                />
              </div>
            </FlexContainer>

            {values.redirectType === 'url' && (
              <Field
                id="redirectUrl"
                name="redirectUrl"
                placeholder={t('templates.linkOr')}
                className="w-full"
                component={FormikInput}
              />
            )}
            {values.redirectType === 'template' && (
              <RedirectPageSelectInput
                className="w-full"
                defaultValue={values.redirectPage}
                onChange={(template: PageTemplate) => {
                  setFieldValue('redirectPage', template);
                }}
                additionalFilters={[
                  ...activeFilters,
                  group(GroupCondition.OR, [
                    equal('account', account?.id),
                    equal('account', null),
                  ]),
                ]}
              />
            )}
          </StyledDiv>
        </div>
        {isSystem && (
          <>
            <div className="field-checkbox">
              <Field
                inputId="active"
                name="active"
                label={t('templates.set.active')}
                component={FormikSwitch}
              />
            </div>
            <div className="field-checkbox">
              <Field
                inputId="usedForAiGeneratedCampaignTemplate"
                name="usedForAiGeneratedCampaignTemplate"
                label={t('templates.usedForAiGenerated')}
                component={FormikSwitch}
              />
            </div>
          </>
        )}

        {values.meta?.cloneOf && (
          <>
            <h3 className="mb-4">{t('generic.cloneOf')}</h3>

            <div className="field w-6">
              <label htmlFor="cloneOf">{t('templates.pageTemplate')}</label>
              <InputText
                id="cloneOf"
                name="cloneOf"
                value={values.meta?.cloneOf}
                className="w-full"
                disabled
              />
            </div>

            <div className="field w-6">
              <label htmlFor="sourceCampaignId">{t('campaign')}</label>
              <InputText
                id="sourceCampaignId"
                name="sourceCampaignId"
                value={values.meta?.sourceCampaignId}
                className="w-full"
                disabled
              />
            </div>
          </>
        )}
      </Form>
    </FlexContainer>
  );
};

export const LandingPageTemplateInformationForm: React.FC<
  LandingPageTemplateInformationFormProps
> = ({ initialValues, onSubmit, onInputChange, onValidateForm, isSystem }) => {
  const { t } = useTranslation();

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={landingPageTemplateSchema(t)}
      onSubmit={onSubmit}
      validateOnMount
    >
      <LandingFormFields
        onInputChange={onInputChange}
        onValidateForm={onValidateForm}
        isSystem={isSystem}
      />
    </Formik>
  );
};
